/**
 * Mega Menu Navigation JavaScript
 * Enhanced navigation with mega menu functionality
 */

class MegaMenuNavigation {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupMobileMenu();
    this.setupAccessibility();
    this.setupStickyNavigation();
    this.setupPerformanceOptimizations();
  }

  setupEventListeners() {
    const megaNavItems = document.querySelectorAll('.mega-nav-item');
    const blackout = document.querySelector('.mega-nav-blackout');

    megaNavItems.forEach(item => {
      const dropdown = item.querySelector('.mega-menu-dropdown');
      const link = item.querySelector('.mega-nav-link');

      if (dropdown) {
        this.setupDropdownEvents(item, dropdown, link, blackout);
      }
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mega-nav-item')) {
        this.closeAllDropdowns();
      }
    });

    // Close dropdowns on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
        // Return focus to the last focused menu item
        const focusedItem = document.querySelector('.mega-nav-link:focus');
        if (focusedItem) {
          focusedItem.focus();
        }
      }
    });
  }

  setupDropdownEvents(item, dropdown, link, blackout) {
    let hoverTimeout;
    let isDropdownOpen = false;

    // Mouse events for desktop
    item.addEventListener('mouseenter', () => {
      if (window.innerWidth > 989) {
        clearTimeout(hoverTimeout);
        this.openDropdown(item, dropdown, blackout);
        isDropdownOpen = true;
      }
    });

    item.addEventListener('mouseleave', () => {
      if (window.innerWidth > 989) {
        hoverTimeout = setTimeout(() => {
          this.closeDropdown(item, dropdown, blackout);
          isDropdownOpen = false;
        }, 150);
      }
    });

    // Click events for mobile
    link.addEventListener('click', (e) => {
      if (window.innerWidth <= 989 && dropdown) {
        e.preventDefault();
        
        if (isDropdownOpen) {
          this.closeDropdown(item, dropdown);
          isDropdownOpen = false;
        } else {
          this.closeAllDropdowns();
          this.openDropdown(item, dropdown);
          isDropdownOpen = true;
        }
      }
    });

    // Keyboard navigation
    link.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          if (dropdown) {
            e.preventDefault();
            if (isDropdownOpen) {
              this.closeDropdown(item, dropdown, blackout);
              isDropdownOpen = false;
            } else {
              this.closeAllDropdowns();
              this.openDropdown(item, dropdown, blackout);
              isDropdownOpen = true;
              // Focus first link in dropdown
              const firstLink = dropdown.querySelector('.mega-menu-column-title, .mega-menu-link');
              if (firstLink) {
                setTimeout(() => firstLink.focus(), 100);
              }
            }
          }
          break;
        case 'ArrowDown':
          if (dropdown && !isDropdownOpen) {
            e.preventDefault();
            this.openDropdown(item, dropdown, blackout);
            isDropdownOpen = true;
            const firstLink = dropdown.querySelector('.mega-menu-column-title, .mega-menu-link');
            if (firstLink) {
              setTimeout(() => firstLink.focus(), 100);
            }
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.focusNextMenuItem(item);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.focusPreviousMenuItem(item);
          break;
      }
    });

    // Keyboard navigation within dropdown
    if (dropdown) {
      dropdown.addEventListener('keydown', (e) => {
        const focusableElements = dropdown.querySelectorAll('.mega-menu-column-title, .mega-menu-link, .mega-menu-promo');
        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % focusableElements.length;
            focusableElements[nextIndex].focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            const prevIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
            focusableElements[prevIndex].focus();
            break;
          case 'Tab':
            if (e.shiftKey && currentIndex === 0) {
              // Tab backwards from first item - close dropdown and focus menu link
              e.preventDefault();
              this.closeDropdown(item, dropdown, blackout);
              link.focus();
              isDropdownOpen = false;
            } else if (!e.shiftKey && currentIndex === focusableElements.length - 1) {
              // Tab forwards from last item - close dropdown
              this.closeDropdown(item, dropdown, blackout);
              isDropdownOpen = false;
            }
            break;
        }
      });
    }
  }

  openDropdown(item, dropdown, blackout) {
    // Close other dropdowns first
    this.closeAllDropdowns();

    dropdown.style.opacity = '1';
    dropdown.style.visibility = 'visible';
    dropdown.style.transform = window.innerWidth > 989 
      ? 'translateX(-50%) translateY(0)' 
      : 'none';
    dropdown.classList.add('mega-menu-fade-in');
    dropdown.classList.remove('mega-menu-fade-out');

    if (window.innerWidth <= 989) {
      dropdown.classList.add('mobile-open');
      dropdown.style.display = 'block';
    }

    // Show blackout on desktop
    if (blackout && window.innerWidth > 989) {
      blackout.style.opacity = '1';
      blackout.style.visibility = 'visible';
      blackout.classList.add('active');
    }

    // Set ARIA attributes
    const link = item.querySelector('.mega-nav-link');
    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }
    dropdown.setAttribute('aria-hidden', 'false');

    // Announce to screen readers
    this.announceToScreenReader('Submenu opened');
  }

  closeDropdown(item, dropdown, blackout) {
    dropdown.style.opacity = '0';
    dropdown.style.visibility = 'hidden';
    dropdown.style.transform = window.innerWidth > 989 
      ? 'translateX(-50%) translateY(-10px)' 
      : 'none';
    dropdown.classList.add('mega-menu-fade-out');
    dropdown.classList.remove('mega-menu-fade-in');

    if (window.innerWidth <= 989) {
      dropdown.classList.remove('mobile-open');
      setTimeout(() => {
        if (!dropdown.classList.contains('mobile-open')) {
          dropdown.style.display = 'none';
        }
      }, 300);
    }

    // Hide blackout
    if (blackout) {
      blackout.style.opacity = '0';
      blackout.style.visibility = 'hidden';
      blackout.classList.remove('active');
    }

    // Set ARIA attributes
    const link = item.querySelector('.mega-nav-link');
    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }
    dropdown.setAttribute('aria-hidden', 'true');
  }

  closeAllDropdowns() {
    const allDropdowns = document.querySelectorAll('.mega-menu-dropdown');
    const blackout = document.querySelector('.mega-nav-blackout');

    allDropdowns.forEach(dropdown => {
      const item = dropdown.closest('.mega-nav-item');
      this.closeDropdown(item, dropdown, blackout);
    });
  }

  focusNextMenuItem(currentItem) {
    const menuItems = Array.from(document.querySelectorAll('.mega-nav-item'));
    const currentIndex = menuItems.indexOf(currentItem);
    const nextIndex = (currentIndex + 1) % menuItems.length;
    const nextLink = menuItems[nextIndex].querySelector('.mega-nav-link');
    if (nextLink) {
      nextLink.focus();
    }
  }

  focusPreviousMenuItem(currentItem) {
    const menuItems = Array.from(document.querySelectorAll('.mega-nav-item'));
    const currentIndex = menuItems.indexOf(currentItem);
    const prevIndex = currentIndex === 0 ? menuItems.length - 1 : currentIndex - 1;
    const prevLink = menuItems[prevIndex].querySelector('.mega-nav-link');
    if (prevLink) {
      prevLink.focus();
    }
  }

  setupMobileMenu() {
    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth > 989) {
          // Desktop mode - close mobile dropdowns
          const mobileDropdowns = document.querySelectorAll('.mega-menu-dropdown.mobile-open');
          mobileDropdowns.forEach(dropdown => {
            dropdown.classList.remove('mobile-open');
            dropdown.style.display = '';
          });
        } else {
          // Mobile mode - close desktop dropdowns
          this.closeAllDropdowns();
        }
      }, 250);
    });
  }

  setupAccessibility() {
    // Set initial ARIA attributes
    const menuLinks = document.querySelectorAll('.mega-nav-link');
    const dropdowns = document.querySelectorAll('.mega-menu-dropdown');

    menuLinks.forEach(link => {
      const dropdown = link.parentElement.querySelector('.mega-menu-dropdown');
      if (dropdown) {
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('role', 'button');
      }
    });

    dropdowns.forEach(dropdown => {
      dropdown.setAttribute('aria-hidden', 'true');
      dropdown.setAttribute('role', 'menu');
    });

    // Add role to menu links
    const menuLinksInDropdown = document.querySelectorAll('.mega-menu-link, .mega-menu-column-title');
    menuLinksInDropdown.forEach(link => {
      link.setAttribute('role', 'menuitem');
    });
  }

  setupStickyNavigation() {
    const navSection = document.querySelector('.mega-menu-navigation-section');
    if (!navSection || !navSection.classList.contains('sticky')) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateStickyNav = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 100) {
        navSection.classList.add('scrolled');
      } else {
        navSection.classList.remove('scrolled');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateStickyNav);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  setupPerformanceOptimizations() {
    // Lazy load dropdown content
    const dropdowns = document.querySelectorAll('.mega-menu-dropdown');
    
    dropdowns.forEach(dropdown => {
      const images = dropdown.querySelectorAll('img[data-src]');
      
      const loadImages = () => {
        images.forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
        });
      };

      // Load images when dropdown is first opened
      const item = dropdown.closest('.mega-nav-item');
      item.addEventListener('mouseenter', loadImages, { once: true });
      item.addEventListener('focusin', loadImages, { once: true });
    });

    // Preload critical dropdown content on hover intent
    const menuItems = document.querySelectorAll('.mega-nav-item');
    
    menuItems.forEach(item => {
      let hoverIntent = false;
      let hoverTimeout;

      item.addEventListener('mouseenter', () => {
        hoverTimeout = setTimeout(() => {
          hoverIntent = true;
          // Preload dropdown content here if needed
        }, 100);
      });

      item.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        hoverIntent = false;
      });
    });
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  // Public methods for external control
  openMenu(menuItemSelector) {
    const item = document.querySelector(menuItemSelector);
    if (item) {
      const dropdown = item.querySelector('.mega-menu-dropdown');
      const blackout = document.querySelector('.mega-nav-blackout');
      if (dropdown) {
        this.openDropdown(item, dropdown, blackout);
      }
    }
  }

  closeMenu(menuItemSelector) {
    const item = document.querySelector(menuItemSelector);
    if (item) {
      const dropdown = item.querySelector('.mega-menu-dropdown');
      const blackout = document.querySelector('.mega-nav-blackout');
      if (dropdown) {
        this.closeDropdown(item, dropdown, blackout);
      }
    }
  }

  closeAllMenus() {
    this.closeAllDropdowns();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenuNavigation();
});

// Add CSS for screen reader only content
if (!document.querySelector('#mega-menu-sr-styles')) {
  const srStyles = document.createElement('style');
  srStyles.id = 'mega-menu-sr-styles';
  srStyles.textContent = `
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;
  document.head.appendChild(srStyles);
}

// Export for use in other scripts
window.MegaMenuNavigation = MegaMenuNavigation;
